import { useConversationContext } from "~/pages/manifold-nextgen/components/conversations/ConversationContext";
import {
  NEXTGEN_CHAT_EVENT,
  trackNextgenChatEvent,
} from "~/pages/manifold-nextgen/components/conversations/analytics";
import {
  artifactDownloadPath,
  artifactExportPath,
  artifactMethodsDocumentPath,
  type ArtifactDownloadResult,
  type ArtifactExportResult,
  type ArtifactMethodsDocument,
  type ArtifactType,
  type ConvertAndCopyFormat,
  type MethodsDocumentFormat,
} from "~/services/artifacts";
import { apiClient, apiMutate } from "~/utils/apiClient";
import { METHODS_OPTIONS } from "./formats";

const METHODS_CONTENT_TYPES: Record<MethodsDocumentFormat, string> = {
  markdown: "text/markdown",
  notebook: "application/x-ipynb+json",
};

export type DownloadResult = { ok: true } | { ok: false; error: string };

const saveBlob = (blob: Blob, fileName: string) => {
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objectUrl);
};

export type ExportResult =
  | { ok: true; result: ArtifactExportResult }
  | { ok: false; error: string };

// Per-artifact mutating actions: download (fetch presigned URL + trigger
// browser save), download the methods document (the recipe that rebuilds the
// artifact), and export-to-workbench (drop a converted copy into a
// workbench project's exports directory). Stateless — each call returns a
// result object so callers can manage their own stages and error UI.
//
// The workbench project id is supplied per call (selected in the export
// dialog) because RWB workbench projects are an independent identifier
// space from AI projects — the conversation has no implicit workbench.
export function useArtifactActions(artifactId: string, artifactType: ArtifactType) {
  const { conversationId } = useConversationContext();

  const download = async (format: ConvertAndCopyFormat): Promise<DownloadResult> => {
    if (!conversationId) {
      return { ok: false, error: "Downloads are not available in this view." };
    }
    try {
      const result = await apiMutate<ArtifactDownloadResult>(
        artifactDownloadPath(conversationId, artifactId),
        "POST",
        { format }
      );
      // Pull the bytes ourselves so the browser uses our filename rather
      // than whatever the presigned URL hands back via Content-Disposition.
      const res = await fetch(result.download_url);
      if (!res.ok) throw new Error(`Download failed with status ${res.status}`);
      saveBlob(await res.blob(), result.file_name);
      trackNextgenChatEvent(
        NEXTGEN_CHAT_EVENT.ARTIFACT_DOWNLOADED,
        { conversationId },
        { artifact_id: artifactId, artifact_type: artifactType, format }
      );
      return { ok: true };
    } catch (err) {
      const error = err instanceof Error ? err.message : String(err);
      trackNextgenChatEvent(
        NEXTGEN_CHAT_EVENT.ARTIFACT_DOWNLOAD_FAILED,
        { conversationId },
        { artifact_id: artifactId, artifact_type: artifactType, format, error }
      );
      return { ok: false, error };
    }
  };

  const downloadMethodsDocument = async (
    documentFormat: MethodsDocumentFormat
  ): Promise<DownloadResult> => {
    if (!conversationId) {
      return { ok: false, error: "Downloads are not available in this view." };
    }
    const format = METHODS_OPTIONS.find((o) => o.documentFormat === documentFormat)!.value;
    try {
      const methods = await apiClient<ArtifactMethodsDocument>(
        artifactMethodsDocumentPath(conversationId, artifactId, documentFormat)
      );
      saveBlob(
        new Blob([methods.content], { type: METHODS_CONTENT_TYPES[documentFormat] }),
        methods.file_name
      );
      trackNextgenChatEvent(
        NEXTGEN_CHAT_EVENT.ARTIFACT_DOWNLOADED,
        { conversationId },
        { artifact_id: artifactId, artifact_type: artifactType, format }
      );
      return { ok: true };
    } catch (err) {
      const error = err instanceof Error ? err.message : String(err);
      trackNextgenChatEvent(
        NEXTGEN_CHAT_EVENT.ARTIFACT_DOWNLOAD_FAILED,
        { conversationId },
        { artifact_id: artifactId, artifact_type: artifactType, format, error }
      );
      return { ok: false, error };
    }
  };

  const exportToWorkbench = async (
    format: ConvertAndCopyFormat,
    workbenchProjectId: number
  ): Promise<ExportResult> => {
    if (!conversationId) {
      return { ok: false, error: "Exports are not available in this view." };
    }
    try {
      const result = await apiMutate<ArtifactExportResult>(
        artifactExportPath(conversationId, artifactId),
        "POST",
        { project_id: workbenchProjectId, format }
      );
      trackNextgenChatEvent(
        NEXTGEN_CHAT_EVENT.ARTIFACT_EXPORT_COMPLETED,
        { conversationId },
        {
          artifact_id: artifactId,
          artifact_type: artifactType,
          format,
          workbench_project_id: workbenchProjectId,
        }
      );
      return { ok: true, result };
    } catch (err) {
      const error = err instanceof Error ? err.message : String(err);
      trackNextgenChatEvent(
        NEXTGEN_CHAT_EVENT.ARTIFACT_EXPORT_FAILED,
        { conversationId },
        {
          artifact_id: artifactId,
          artifact_type: artifactType,
          format,
          workbench_project_id: workbenchProjectId,
          error,
        }
      );
      return { ok: false, error };
    }
  };

  return {
    download,
    downloadMethodsDocument,
    exportToWorkbench,
  };
}
